import * as Sentry from '@sentry/react';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAccountParams } from '../../../../hooks/useAccountParams';
import { customFetch } from '../../../../utils/customFetch';
import { IASSegment, IASSegmentsResponse } from './types';

const fetchClientIasRules = async (accountId?: number) => {
  if (!accountId) {
    throw new Error('Account ID is required');
  }
  const { data } = await customFetch<IASSegmentsResponse>(
    `/authenticated/client/${accountId}/ias/rules`,
    { useNodeEndpoint: true },
  );

  return data;
};

export const getClientIASRulesOptions = <T = IASSegmentsResponse>(
  accountId?: number,
  select?: (data: IASSegmentsResponse) => T,
) =>
  queryOptions({
    queryKey: ['clientIASRules', accountId],
    queryFn: () => fetchClientIasRules(accountId),
    enabled: !!accountId,
    select,
  });

export function useClientIASRules<T = IASSegmentsResponse>(
  select?: (data: IASSegmentsResponse) => T,
) {
  const accountParams = useAccountParams();
  // eslint-disable-next-line @tanstack/query/prefer-query-object-syntax
  return useQuery(getClientIASRulesOptions(accountParams?.accountId, select));
}

const fetchClientIASSegments = async (accountId?: number) => {
  if (!accountId) {
    throw new Error('Account ID is required');
  }
  const {
    data: { segments },
  } = await customFetch<{ segments: IASSegment[] }>(
    `/authenticated/client/${accountId}/ias/segments`,
    {
      useNodeEndpoint: true,
    },
  );

  return segments;
};

export const getClientIASSegmentsOptions = (accountId?: number) =>
  queryOptions({
    queryKey: ['clientIASSegments', accountId],
    queryFn: () => fetchClientIASSegments(accountId),
    enabled: !!accountId,
  });

export function useClientIASSegments() {
  const accountParams = useAccountParams();
  // eslint-disable-next-line @tanstack/query/prefer-query-object-syntax
  return useQuery(getClientIASSegmentsOptions(accountParams?.accountId));
}

type ClientIASRulesMutationArgs = {
  brandSafety: Record<string, string>;
  segmentIds: number[];
  blockIasInvalidIps: number | null;
};

export function useClientIASRulesMutation() {
  const accountParams = useAccountParams();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rules: ClientIASRulesMutationArgs) =>
      customFetch(`/authenticated/client/${accountParams?.accountId}/ias/rules`, {
        method: 'POST',
        body: JSON.stringify(rules),
        useNodeEndpoint: true,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries(getClientIASRulesOptions(accountParams?.accountId));
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });
}

type CreateIASSegmentArgs = {
  description: string;
  name: string;
  segmentId: number;
};

type CreateIASSegmentResponse =
  | {
      success: false;
      error: string;
    }
  | {
      success: true;
      segment: {
        id: number;
        segmentId: number;
        description: string;
        name: string;
      };
    };

export function useCreateIASSegmentMutation() {
  const accountParams = useAccountParams();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (segment: CreateIASSegmentArgs) =>
      customFetch<CreateIASSegmentResponse>(
        `/authenticated/client/${accountParams?.accountId}/ias/segments`,
        {
          method: 'POST',
          body: JSON.stringify(segment),
          useNodeEndpoint: true,
          throwOnHTTPError: false,
        },
      ).then((res) => {
        if (!res.data.success) {
          throw new Error(res.data.error);
        }
        return res.data.segment;
      }),
    onMutate: async (segment) => {
      await queryClient.cancelQueries(getClientIASSegmentsOptions(accountParams?.accountId));
      const segmentOptionsQuery = getClientIASSegmentsOptions(accountParams?.accountId);
      const previousSegments = queryClient.getQueryData(segmentOptionsQuery.queryKey);
      queryClient.setQueryData(segmentOptionsQuery.queryKey, (old) => {
        return [
          ...(old || []),
          {
            clientId: accountParams?.accountId || null,
            description: segment.description,
            name: segment.name,
            segmentId: segment.segmentId,
            // these aren't used but are required by the type
            channel: '',
            id: 0,
            segmentCode: '',
            type: 'IAS',
            format: 'IAS',
          } satisfies IASSegment,
        ];
      });
      return { previousSegments };
    },

    onError: (error, variables, context) => {
      const segmentOptionsQuery = getClientIASSegmentsOptions(accountParams?.accountId);
      Sentry.captureException(error);
      if (context?.previousSegments) {
        queryClient.setQueryData(segmentOptionsQuery.queryKey, context.previousSegments);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries(getClientIASSegmentsOptions(accountParams?.accountId));
    },
  });
}
